import { setCookie, removeCookies } from 'cookies-next'
import { isArray, isEmpty } from 'lodash'
import qs from 'querystring'
import { api } from '../apis/Axios'
import { UserAPI } from '../apis/UserAPI'
import { handleWooCookiesForCart } from './auth'
import {
    getWooSessionKeysFromObject,
    log,
    wooCommerceSessionKey,
} from './utils'

export const sessionKeyName = 'olm-jwt'
export const impersonateSessionKeyName = 'olm-impersonate-jwt'
export const impersonatedByEmailKeyName = 'olm-impersonated-by-email'
export const userKey = 'olm-user'

export function parseCookie(req) {
    return _parseCookie(req.headers.cookie)
}

export function _parseCookie(cookie) {
    return qs.decode(cookie, '; ')
}

export const getCookies = (cookiesArray) => {
    let cookies = []
    if (isArray(cookiesArray)) {
        for (const cookie of cookiesArray) {
            const value = cookie.split(';')[0]
            if (!cookies.includes(value)) {
                cookies.push(encodeURI(value))
            }
        }
        return cookies.join(';')
    }
    return ''
}

export const expiredCode = 14

export const getJWT = (req) => {
    const cookies = parseCookie(req)
    // log('getJWT', cookies)
    return cookies[impersonateSessionKeyName] || cookies[sessionKeyName]
}

export const getOnlySessionJWT = (req) => {
    const cookies = parseCookie(req)
    return cookies[sessionKeyName]
}

export const getWooSessionKeys = async (req) => {
    await handleWooCookiesForCart(req)
    const cookies = parseCookie(req)
    return getWooSessionKeysFromObject(cookies)
}

export const getLoggedInUser = async (req) => {
    try {
        const { data } = await UserAPI.checklogin(getJWT(req))
        return data
    } catch (e) {
        return null
    }
}

export const getHeaders = (headers) => {
    const nonce = headers['nonce']
    const Cookie = prepareCookie(getCookies(headers['set-cookie'])) || ''
    return {
        nonce,
        Cookie,
    }
}

export const prepareCookie = (cookie) => {
    return decodeURI(decodeURI(cookie))
}

export const isValidWooCookies = (wooCookies) =>
    !isEmpty(wooCookies) &&
    wooCookies?.['Cookie'] !== '' &&
    wooCookies?.['Cookie'].includes(WP_SESSION_KEY)

export const getSessionKey = (wooCookies) => {
    let session_key
    if (isValidWooCookies(wooCookies)) {
        session_key = getSessionKeyFromWooSessionCookie(
            findWooSessionCookie(wooCookies?.['Cookie']),
        )
    }
    return session_key
}

export const ForCheckoutProduct = process.env?.NEXT_PUBLIC_BACKEND?.includes(
    'develop',
)
    ? 195494 //189599
    : process.env?.NEXT_PUBLIC_BACKEND?.includes('admin')
    ? 195494
    : process.env?.NEXT_PUBLIC_BACKEND?.includes('staging')
    ? 195494
    : 1080

export const WP_SESSION_KEY = process.env?.NEXT_PUBLIC_BACKEND?.includes(
    'develop',
)
    ? // ? 'wp_woocommerce_session_fd3dab572136f4ba10497189bc4c37cc'
      'wp_woocommerce_session_d7428e21891935a549eaaab5c6b04da7'
    : process.env?.NEXT_PUBLIC_BACKEND?.includes('staging')
    ? 'wp_woocommerce_session_9e0f5da988035424fb1ba61108858fff'
    : 'wp_woocommerce_session_f61c4decbaad98f4ec4bf2dd7fc28120'

export const findWooSessionCookie = (cookieString = '') => {
    return String(cookieString)
        .split(';')
        .map((item) => item.trim())
        .find((item) => item.startsWith(`${WP_SESSION_KEY}=`))
}

export const getSessionKeyFromWooSessionCookie = (sessionCookie) => {
    if (!sessionCookie) return undefined

    const token = sessionCookie.replace(`${WP_SESSION_KEY}=`, '')
    const separator = token.includes('||') ? '||' : '%7C%7C'
    return token.split(separator)[0]
}

export const getWooCookiesFromServer = async (customer_id, session_key) => {
    const { data: wooData } = await getWPSession({
        session_key,
        customer_id,
        action: 'get-wp-session',
    })
    const wooSessionKeyValue = wooData['cookie_full_value']['data']
    return wooSessionKeyValue
}

export const getWooCookies = async (customer_id, req) => {
    const wooCookies = await getWooSessionKeys(req)

    let session_key = customer_id

    if (isValidWooCookies(wooCookies)) {
        session_key = getSessionKeyFromWooSessionCookie(
            findWooSessionCookie(wooCookies?.['Cookie']),
        )
    }

    const wooSessionKeyValue = await getWooCookiesFromServer(
        customer_id,
        session_key,
    )

    return { session_key, wooSessionKeyValue }
}

export const handleWooCommerceSessionKey = async (customer_id, req, res) => {
    try {
        const { session_key, wooSessionKeyValue } = await getWooCookies(
            customer_id,
            req,
        )

        /**
         * session key is never null if its null, we better remove the cookie
         */
        if (!session_key && !customer_id) {
            removeCookies(wooCommerceSessionKey, { req, res })
        } else if (wooSessionKeyValue) {
            setCookie(wooCommerceSessionKey, wooSessionKeyValue, {
                req,
                res,
                httpOnly: true,
            })
        }
    } catch (e) {
        console.log(e)
    }
}

export const deleteCartFromDB = async (req, res) => {
    const wooCookies = await getWooSessionKeys(req)

    let session_key

    if (isValidWooCookies(wooCookies)) {
        session_key = getSessionKeyFromWooSessionCookie(
            findWooSessionCookie(wooCookies?.['Cookie']),
        )
    }

    if (session_key) {
        await getWPSession({
            session_key,
            customer_id: session_key,
            action: 'delete-cart',
        })
        log(
            'cart deleted',
            `olm-plugin/v1/delete-cart?session_key=${session_key}&customer_id=${session_key}`,
        )
        setCookie(wooCommerceSessionKey, {}, { req, res })
    }
}

const getWPSession = ({ session_key, customer_id, action }) => {
    return api.post(`olm-plugin/v1/${action}`, {
        session_key,
        customer_id,
        woo_key: process.env.WOO_KEY,
        woo_secret: process.env.WOO_SECRET,
    })
}

export const handleCartAfterLogin = async (cookies, customer_id) => {
    const session_key = getSessionKeyFromWooSessionCookie(
        findWooSessionCookie(cookies[wooCommerceSessionKey]),
    )

    if (session_key) {
        // log('session_key', { session_key, customer_id })
        return await getWPSession({
            session_key,
            customer_id,
            action: 'update-cart-on-login',
        })
    }
}
